import { colors } from "@indeed/ifl-tokens/react-native";
import React, { type RefObject, forwardRef, useMemo, useRef, useState, useCallback } from "react";
import { TextInput, type TextInputProps } from "react-native";

import { Text, Alert, Flex, Pressable, type BoxRef } from "../../../mads";
import { Close, Hide } from "../../../mads/icons";
import { useI18n, useLocale, type GettextInterface } from "../../I18n";
import { serializeLocaleToBCP47 } from "../../I18n/Locale";
import { getLocaleCurrency } from "../../util/salary/getLocaleCurrency";
import { parseLocaleCurrencyAmount } from "../../util/salary/parseLocaleCurrencyAmount";
import { SalaryType } from "../../util/salary/SalaryType";
import { FormField, PayPeriodDropdown } from "./components";
import { styles } from "./styles";
import {
  validatePayRange,
  type SalaryPreference,
  type PayBoundsMap,
  filterPayInput,
  isValidPayInput,
  formatPayInputForDisplay,
} from "./validation";

export type { SalaryPreference, PayBoundsMap } from "./validation";

export interface MinimumPayProps {
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  showErrors?: boolean;
  showEmployerAlert?: boolean;
  variant?: "default" | "compact";
  /** Per-pay-period bounds override (homepage's server-driven ranges); falls back to the per-country table. */
  payBounds?: PayBoundsMap;
  /** Whether an amount is required. Defaults to `true`; `false` allows an empty amount (e.g. skippable onboarding). */
  required?: boolean;
  /** Helper text rendered under the "Minimum base pay" label. */
  description?: string;
  /** Ref forwarded to the amount TextInput for focus management. */
  amountInputRef?: RefObject<TextInput | null>;
  /** Focus handler for the amount TextInput. */
  onAmountFocus?: TextInputProps["onFocus"];
  /** Ref forwarded to the pay-period dropdown trigger for focus management. */
  payPeriodRef?: RefObject<BoxRef | null>;
  /** Test id for the amount TextInput. */
  amountInputTestID?: string;
  /** Test id for the pay-period dropdown. */
  payPeriodTestID?: string;
  /** Disables the amount input and pay-period dropdown (e.g. during save/delete). */
  disabled?: boolean;
  /** `nativeID` for the amount input's iOS `inputAccessoryViewID`, letting a consumer render a keyboard toolbar. iOS only. */
  inputAccessoryViewID?: string;
}

// PayInput component for the currency input field
export const PayInput = forwardRef<
  TextInput,
  TextInputProps & {
    invalid: boolean;
    /** When set (and the field is editable with a value), shows a clear button with this label. */
    clearAccessibilityLabel?: string;
  }
>(
  (
    {
      invalid,
      clearAccessibilityLabel,
      value,
      onChangeText,
      editable,
      onFocus,
      onBlur,
      ...inputProps
    },
    ref,
  ) => {
    const localeObj = useLocale();
    const locale = serializeLocaleToBCP47(localeObj);
    const { hasRighthandSymbol, currencySymbol } = getLocaleCurrency(locale);
    // Presentational: the currency is conveyed to screen readers via the input's accessibilityHint,
    // so the glyph itself is hidden from assistive tech (otherwise it's read as stray text).
    const currencyElement = (
      <Text sx={styles.payInputCurrency} accessible={false} ariaHidden>
        {currencySymbol}
      </Text>
    );
    // Track focus so the wrapper (not the inner input) renders the web focus ring, wrapping the whole
    // field — currency glyph + input + clear button — instead of just the middle input.
    const [isFocused, setIsFocused] = useState(false);
    const wrapperStyles = {
      ...styles.payInputWrapper,
      ...(invalid ? styles.payInputWrapperInvalid : {}),
      ...(isFocused ? styles.payInputWrapperFocused : {}),
    };

    // Own the TextInput ref internally so the clear button can restore focus after clearing, while
    // still forwarding it for external focus management.
    const innerRef = useRef<TextInput | null>(null);
    const setInputRef = useCallback(
      (node: TextInput | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const showClearButton =
      editable !== false && !!clearAccessibilityLabel && !!onChangeText && !!value;

    return (
      <Flex sx={wrapperStyles}>
        {!hasRighthandSymbol && currencyElement}
        <TextInput
          ref={setInputRef}
          style={styles.payInput}
          inputMode="decimal"
          value={value}
          onChangeText={onChangeText}
          editable={editable}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          {...inputProps}
        />
        {hasRighthandSymbol && currencyElement}
        {showClearButton && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={clearAccessibilityLabel}
            sx={styles.payInputClearButton}
            onPress={() => {
              onChangeText?.("");
              innerRef.current?.focus();
            }}
          >
            <Close size="sm" aria-hidden />
          </Pressable>
        )}
      </Flex>
    );
  },
);

PayInput.displayName = "PayInput";

// EmployersPayInfoAlert component
export const EmployersPayInfoAlert = () => {
  const gt = useI18n();

  return (
    <Alert
      icon={<Hide color={colors.foregroundStatusInfo} aria-hidden />}
      status="info"
      explainerText={gt.gettext("Employers won't see this preference.")}
    />
  );
};

/** Pay period options in display order (GraphQL SalaryType enum values). */
const PAY_PERIOD_ORDER = [
  SalaryType.Hourly,
  SalaryType.Yearly,
  SalaryType.Monthly,
  SalaryType.Weekly,
  SalaryType.Daily,
] as const;

// PayPeriodSelect component for selecting the pay period. A dropdown rather than chips: every
// period stays reachable without a show more/less toggle that can hide the selection.
export const PayPeriodSelect = ({
  invalid,
  value,
  onChange,
  errorText,
  showRequiredIndicator = false,
  triggerRef,
  testID,
  disabled,
}: {
  invalid?: boolean;
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  errorText?: string;
  showRequiredIndicator?: boolean;
  /** Ref forwarded to the dropdown trigger for focus management (e.g. on a validation error). */
  triggerRef?: RefObject<BoxRef | null>;
  testID?: string;
  disabled?: boolean;
}) => {
  const amount = value?.amount ?? "";

  const selectSalaryType = useCallback(
    (selected: string) => onChange({ amount, salaryType: selected, sentiment: value?.sentiment }),
    [amount, onChange, value?.sentiment],
  );

  return (
    <PayPeriodDropdown
      periods={PAY_PERIOD_ORDER}
      value={value?.salaryType ?? ""}
      onChange={selectSalaryType}
      invalid={invalid}
      errorText={errorText}
      showRequiredIndicator={showRequiredIndicator}
      triggerRef={triggerRef}
      testID={testID}
      disabled={disabled}
    />
  );
};

// Helper function to get error messages
export function getPayErrorMessages({
  gt,
  value,
  showErrors,
  locale,
  payBounds,
  required = true,
}: {
  value?: SalaryPreference;
  gt: GettextInterface;
  showErrors?: boolean;
  locale: string;
  /** Per-pay-period bounds override (homepage's server-driven ranges). */
  payBounds?: PayBoundsMap;
  /** Whether an amount is required. Defaults to `true`; when `false` an empty amount is allowed (a zero still errors). */
  required?: boolean;
}) {
  // Reject a non-empty amount that doesn't parse to a finite number (a lone "." or ","); otherwise
  // homepage would persist it as 0. Treated like a zero.
  const parsedAmount = value?.amount ? parseLocaleCurrencyAmount(value.amount, locale) : Number.NaN;
  const isAmountInvalidOrZero =
    !!value?.amount && (!Number.isFinite(parsedAmount) || parsedAmount === 0);
  const payRangeValidationError =
    (value && validatePayRange(value, locale, gt, payBounds)) || undefined;

  const shouldShowRequiredError = !value?.amount && required;

  return {
    // Gate the *displayed* range error on showErrors, like inputError/selectError, so an
    // out-of-range amount doesn't render an error on load or mid-typing (submit-only display).
    // Submit gating is unaffected: callers that gate on save pass showErrors: true.
    fieldsetError: showErrors ? payRangeValidationError : undefined,
    inputError:
      showErrors && (isAmountInvalidOrZero || shouldShowRequiredError) && !payRangeValidationError
        ? isAmountInvalidOrZero
          ? gt.pgettext(
              "Minimum pay greater than zero error text",
              "Enter an amount greater than 0",
            )
          : gt.pgettext("Minimum pay required error text", "Minimum pay is required")
        : "",
    selectError:
      // When not required, only demand a period once an amount is entered — a fully empty field is a valid skip.
      showErrors && !value?.salaryType && !payRangeValidationError && (required || !!value?.amount)
        ? gt.pgettext("Pay period error text", "Pay period is required")
        : "",
  };
}

// Main MinimumPay component
export const MinimumPay: React.FC<MinimumPayProps> = ({
  value,
  onChange,
  showErrors,
  showEmployerAlert = true,
  variant = "default",
  payBounds,
  required = true,
  description,
  amountInputRef,
  onAmountFocus,
  payPeriodRef,
  amountInputTestID,
  payPeriodTestID,
  disabled,
  inputAccessoryViewID,
}) => {
  const localeObj = useLocale();
  const locale = serializeLocaleToBCP47(localeObj);
  const gt = useI18n();
  const basePayLabel = gt.gettext("Minimum base pay");

  const { fieldsetError, inputError, selectError } = getPayErrorMessages({
    gt,
    value,
    showErrors,
    locale,
    payBounds,
    required,
  });

  const formattedValue = useMemo(
    // Group the integer part but keep the in-progress decimal; Intl.NumberFormat.format() would drop
    // a trailing separator/zero ("12." → "12"), making decimals impossible to type.
    () => (value?.amount ? formatPayInputForDisplay(value.amount, locale) : ""),
    [value?.amount, locale],
  );

  const handleAmountChange = useCallback(
    (text: string) => {
      // Strip locale group separators before validating; the displayed value carries them (e.g.
      // "5,000"), so validating the raw text would reject typing once a separator appears.
      const filteredAmount = filterPayInput(text, locale);
      if (isValidPayInput(filteredAmount, locale)) {
        onChange({
          amount: filteredAmount,
          salaryType: value?.salaryType,
          sentiment: value?.sentiment,
        });
      }
    },
    [onChange, value?.salaryType, value?.sentiment, locale],
  );

  // inputError (required/zero) and fieldsetError (range) are both amount errors and mutually
  // exclusive; consolidate them so the amount error is rendered once (below the input) and can be
  // associated with the input for accessibility.
  const amountError = inputError || fieldsetError || "";
  const amountErrorId = amountInputTestID
    ? `${amountInputTestID}-error`
    : "minimum-base-pay-amount-error";
  const { currencyCode } = getLocaleCurrency(locale);
  const currencyHint = gt.gettext("Enter minimum pay amount in {0}", [currencyCode]);
  // Native announces this hint on focus; web associates the error via aria-describedby below.
  const amountAccessibilityHint = amountError
    ? `${gt.gettext("Error: ")}${amountError}. ${currencyHint}`
    : currencyHint;

  const containerStyles = variant === "compact" ? styles.compactContainer : styles.defaultContainer;

  return (
    <Flex sx={containerStyles}>
      {showEmployerAlert && <EmployersPayInfoAlert />}
      <Flex sx={styles.updatedWrapper}>
        <Flex sx={{ marginBottom: 4 }}>
          <FormField
            errorText={amountError}
            errorMessageId={amountErrorId}
            helperText={description}
            showRequiredIndicator={required}
            invalid={!!amountError}
            label={basePayLabel}
          >
            <PayInput
              ref={amountInputRef}
              testID={amountInputTestID}
              value={formattedValue}
              onChangeText={handleAmountChange}
              onFocus={onAmountFocus}
              editable={!disabled}
              aria-required={required}
              aria-label={basePayLabel}
              aria-invalid={amountError ? true : undefined}
              aria-describedby={amountError ? amountErrorId : undefined}
              accessibilityHint={amountAccessibilityHint}
              inputAccessoryViewID={inputAccessoryViewID}
              invalid={!!amountError}
              clearAccessibilityLabel={gt.pgettext("Clear pay amount button", "Clear")}
            />
          </FormField>
        </Flex>
        <PayPeriodSelect
          // Only the period's own error marks it invalid. The range error belongs to the amount field;
          // flagging the period too showed a second, text-less error icon under it on web.
          invalid={!!selectError}
          value={value}
          onChange={onChange}
          errorText={selectError}
          showRequiredIndicator={required}
          triggerRef={payPeriodRef}
          testID={payPeriodTestID}
          disabled={disabled}
        />
      </Flex>
    </Flex>
  );
};

export default MinimumPay;
